import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import { POST, GET } from "../../helpers/apiHelper";
import * as action from "./actions";
import { unauthorizeUser, setSyncJobProgress } from "../../helpers/utility";
import i18n from "../../i18n";

const startGwsSyncAsync = async (item) => POST(`/gws-sync/trigger`, item, { idempotent: true });
const loadProgressGwsSyncAsync = async (id) => GET(`/gws-sync/status/${id}`);
const cancelGwsSyncAsync = async (jobId) => GET(`/gws-sync/cancel/${jobId}`);

/**
 * Saga to start Google Workspace sync.
 * Handles sync job initialization.
 * @param {Object} action - Redux action.
 * @param {Object} action.payload - Payload containing municipal data.
 * @param {Object} action.payload.item - data object.
 * @param {Object} action.payload.history - React Router history for navigation.
 */
function* startGwsSync({ payload }) {
  try {
    if (!payload.item) {
      yield put(action.startGwsSyncError("invalid item, cannot be null"));
      return;
    }
    const response = yield call(startGwsSyncAsync, {
      municipalId: payload?.item?.sourced_id,
      syncType: payload?.syncType,
    });
    const jobId = response?.data?.jobId;
    if (jobId) {
      setSyncJobProgress(jobId, "google_workspace", "import");
      const { history } = payload;
      history.push(`/gws-sync/${jobId}`);
    } else {
      yield put(action.startGwsSyncError("No jobId returned from server"));
    }
  } catch (error) {
    yield put(action.startGwsSyncError(error?.response?.data?.errors?.[0]?.detail || error?.message));
  }
}

/**
 * Saga to track Google Workspace sync process progress.
 * @param {Object} action - Redux action.
 * @param {Object} action.payload - Payload containing sync information.
 * @param {string} action.payload.id - Sync job ID.
 */
function* loadProcessGwsSync({ payload }) {
  try {
    const response = yield call(loadProgressGwsSyncAsync, payload.id);
    yield put(action.progressGwsSyncSuccess(response));
  } catch (error) {
    console.log('loadProgressGwsSyncAsync error', error)
    console.log('loadProgressGwsSyncAsync error.response', error?.response)
    console.log('loadProgressGwsSyncAsync error.response.status', error?.response?.status)

    // Handle CORS or 502 errors
    if (error instanceof TypeError || (error?.response && error?.response?.status === 502)) {
      yield put(action.progressGwsSyncError(i18n.t("gwsSync.errorNetwork")));
    } else if (error?.response && error?.response?.status === 401) {
      unauthorizeUser();
    } else {
      yield put(action.progressGwsSyncError(error?.response?.data || error?.message));
    }
  }
}

/**
 * Watcher saga to start sync when START_GWS_SYNC action is dispatched.
 */
export function* watchStartGwsSync() {
  yield takeEvery(action.START_GWS_SYNC, startGwsSync);
}

/**
 * Watcher saga to load sync progress when PROGRESS_GWS_SYNC action is dispatched.
 */
export function* watchProgressGwsSync() {
  yield takeEvery(action.PROGRESS_GWS_SYNC, loadProcessGwsSync);
}

function* performCancelGwsSync({ payload }) {
  try {
    const { jobId, history } = payload;
    yield call(cancelGwsSyncAsync, jobId);
    if (history) {
      history.push("/import-log");
    }
  } catch (error) {
    yield call(unauthorizeUser, error);
  }
}

export function* watchCancelGwsSync() {
  yield takeEvery(action.CANCEL_GWS_SYNC, performCancelGwsSync);
}

/**
 * Root saga that forks all watchers.
 */
function* gwsSyncSaga() {
  yield all([
    fork(watchStartGwsSync),
    fork(watchProgressGwsSync),
    fork(watchCancelGwsSync),
  ]);
}

export default gwsSyncSaga;
